// Тулбар экрана: ScreenToolbar раскладывает слоты в одном порядке, одна высота для всех контролов, меню «View».
import { Fragment, useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { useDismiss } from '@core/ui/dismiss';
import { POPOVER, cn } from '@core/ui/cn';
import { ChevronDown, PanelTop, SlidersHorizontal } from 'lucide-react';
import { TOOLBAR_HEIGHT } from './geometry';

export const CONTROL = 'h-8 min-h-0';

function Toolbar({ children }: { children: ReactNode }) {
  return (
    <div className="flex items-center gap-3 px-4 border-b border-gray-300 bg-white whitespace-nowrap" style={{ height: TOOLBAR_HEIGHT }}>
      {children}
    </div>
  );
}

const ToolbarDivider = () => <span className="w-px h-6 bg-gray-300 shrink-0" aria-hidden />;

/**
 * Один порядок слотов на всех экранах (Глеб, 2026-09-11): действия («+») всегда на одном месте слева,
 * потом что смотрим (подвид, доска), когда (диапазон), как видим (меню View: тумблеры и скрытое, личное и
 * постоянное), чем фильтруем (поиск и чипы — временное сужение), у правого края статус (счётчики, легенда,
 * чипы выделения) и вкладки боковой панели. Пустой слот не оставляет разделителя.
 */
export function ScreenToolbar({ actions, what, when, view, filter, status, side }: { actions?: ReactNode; what?: ReactNode; when?: ReactNode; view?: ReactNode; filter?: ReactNode; status?: ReactNode; side?: ReactNode }) {
  const groups = [actions, what, when, view, filter].filter((g) => g !== null && g !== undefined && g !== false);
  return (
    <Toolbar>
      {groups.map((g, i) => (
        <Fragment key={i}>
          {i > 0 && <ToolbarDivider />}
          <span className="flex items-center gap-2">{g}</span>
        </Fragment>
      ))}
      <span className="ml-auto flex items-center gap-3">{status}</span>
      {side && <><ToolbarDivider />{side}</>}
    </Toolbar>
  );
}


/** Кнопка в тулбаре с выпадающей панелью: закрывается по клику вне и Escape. dataAttr — хук для e2e (на обёртке); role — menu (пункты) или group (контролы). */
export function ToolbarDropdown({ label, icon, title, dataAttr, children, closeSignal, role = 'menu' }: { label: ReactNode; icon: ReactNode; title: string; dataAttr: string; children: ReactNode; closeSignal?: unknown; role?: 'menu' | 'group' }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLSpanElement>(null);
  const close = useCallback(() => setOpen(false), []);
  useDismiss(ref, open, close);
  useEffect(() => { setOpen(false); }, [closeSignal]);
  return (
    <span ref={ref} className="relative inline-flex" {...{ [dataAttr]: '' }}>
      <button
        type="button" aria-haspopup="menu" aria-expanded={open} onClick={() => setOpen(!open)} title={title}
        className={`${CONTROL} px-2 rounded-md text-sm text-gray-600 hover:bg-gray-100 hover:text-gray-800 flex items-center gap-1`}
      >
        {icon} {label} <ChevronDown className="w-3.5 h-3.5" />
      </button>
      {open && <div role={role} className={cn('absolute left-0 top-full mt-1 z-50 min-w-48', POPOVER)}>{children}</div>}
    </span>
  );
}

export const menuItem = 'w-full min-h-0 flex items-center gap-2 px-3 py-1.5 rounded text-left hover:bg-gray-100 whitespace-nowrap disabled:opacity-50 disabled:hover:bg-transparent';

type HiddenItem = { id: string; name: string; color?: string };
export type ViewItem = { key: string; label: string; title: string; checked: boolean; onChange: (v: boolean) => void; disabled?: boolean };

type Flag = [boolean, (v: boolean) => void];
/** Тумблеры вида баров, общие для Projects и People: полные названия, метки, глифы релиза. */
export const barViewItems = ({ fullTitles, badges, releaseMarks }: { fullTitles: Flag; badges: Flag; releaseMarks: Flag }): ViewItem[] => [
  { key: 'titles', label: 'Full titles', title: 'Rows grow so that long titles fit', checked: fullTitles[0], onChange: fullTitles[1] },
  { key: 'badges', label: 'Badges', title: 'Marks on task bars: open questions and design needed', checked: badges[0], onChange: badges[1] },
  { key: 'release', label: 'Release marks', title: '★ key release, ! at risk, ✓ confirmed before the title — what the Release Plan shows', checked: releaseMarks[0], onChange: releaseMarks[1] },
];
type HiddenProps = { items: HiddenItem[]; lanes: HiddenItem[]; onShow: (id: string) => void; onShowLane: (id: string) => void; onShowAll: () => void; rowsLabel: string };
const NOTHING_HIDDEN: HiddenProps = { items: [], lanes: [], rowsLabel: 'Rows', onShow: () => undefined, onShowLane: () => undefined, onShowAll: () => undefined }; // экран без скрываемых строк (лонч)

/**
 * Меню «View ▾»: всё, что про то, как я вижу сетку, в одном месте — личные тумблеры и скрытые строки
 * с дорожками шапки (родственные по смыслу; в строку тулбара они уже не влезали).
 */
export function ViewMenu({ items, hidden = NOTHING_HIDDEN, order }: { items: ViewItem[]; hidden?: HiddenProps; order?: { differs: boolean; onReset: () => void } }) {
  const total = hidden.items.length + hidden.lanes.length + (order?.differs ? 1 : 0);
  const [closed, setClosed] = useState(0);
  const caption = 'px-3 pt-1.5 pb-0.5 text-xs uppercase tracking-wide text-gray-500';
  const done = () => { if (hidden.items.length + hidden.lanes.length === 1) setClosed((n) => n + 1); };
  return (
    <ToolbarDropdown
      label={total ? <>View <span className="text-gray-500">· {total}</span></> : 'View'}
      icon={<SlidersHorizontal className="w-4 h-4" />} title="How you see the grid (only for you)" dataAttr="data-view-menu" closeSignal={closed}
    >
      {items.map((it) => (
        <label key={it.key} className={`${menuItem} ${it.disabled ? 'opacity-50' : ''}`} title={it.title}>
          <input type="checkbox" className="w-4 h-4" checked={it.checked} disabled={it.disabled} onChange={(e) => it.onChange(e.target.checked)} />
          {it.label}
        </label>
      ))}
      {order?.differs && (
        <>
          <div className="my-1 h-px bg-gray-200" />
          <div className={caption}>Order</div>
          <button type="button" role="menuitem" data-order-reset className={menuItem} onClick={() => { order.onReset(); setClosed((n) => n + 1); }} title="Back to the default order set by an admin">
            Your project order differs from the default — reset
          </button>
        </>
      )}
      {hidden.items.length + hidden.lanes.length > 0 && (
        <>
          <div className="my-1 h-px bg-gray-200" />
          <div className={caption}>Hidden {hidden.items.length > 0 ? hidden.rowsLabel.toLowerCase() : ''}</div>
          {hidden.items.map((p) => (
            <button key={p.id} type="button" role="menuitem" data-hidden-item className={menuItem} onClick={() => { hidden.onShow(p.id); done(); }} title="Show again">
              {p.color && <span className="inline-block w-3 h-3 rounded-full shrink-0" style={{ background: p.color }} />}{p.name}
            </button>
          ))}
          {hidden.lanes.length > 0 && <div className={caption}>Hidden header lanes</div>}
          {hidden.lanes.map((l) => (
            <button key={l.id} type="button" role="menuitem" data-hidden-lane className={`${menuItem} text-gray-700`} onClick={() => { hidden.onShowLane(l.id); done(); }} title="Show the lane again">
              <PanelTop className="w-3.5 h-3.5 text-gray-400 shrink-0" />{l.name}
            </button>
          ))}
          {hidden.items.length + hidden.lanes.length > 1 && <button type="button" role="menuitem" className={`${menuItem} font-medium`} onClick={() => { hidden.onShowAll(); setClosed((n) => n + 1); }}>Show all</button>}
        </>
      )}
    </ToolbarDropdown>
  );
}
